/**
 * The words a rule matches — one input each.
 * For Media Devoted | Created By Ciro Autiero
 *
 * One field per word rather than a single box of lines: real words contain
 * commas, brackets and spaces ("[PAUSED - CAP]"), so there is no separator that
 * is safe to split on, and a field with its own delete button makes it obvious
 * how many words the rule actually holds.
 *
 * The rows share one bordered container and are separated by dividers, so a
 * five-word list reads as one control rather than five stacked boxes.
 */

import { FC } from "react";
import { Plus, X } from "lucide-react";

import { Button } from "@/components/ui";

interface Props {
  words: string[];
  onChange: (words: string[]) => void;
}

export const RuleWordList: FC<Props> = ({ words, onChange }) => {
  const rows = words.length > 0 ? words : [""];

  const setAt = (i: number, value: string) =>
    onChange(rows.map((w, idx) => (idx === i ? value : w)));

  const removeAt = (i: number) => {
    const next = rows.filter((_, idx) => idx !== i);
    onChange(next.length > 0 ? next : [""]);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-input bg-background">
      <ul className="divide-y divide-border/60">
        {rows.map((word, i) => (
          <li key={i} className="flex items-center gap-2 pl-3 pr-1.5">
            <span className="w-4 shrink-0 text-xs tabular-nums text-muted-foreground">
              {i + 1}
            </span>
            <input
              value={word}
              onChange={(e) => setAt(i, e.target.value)}
              placeholder={i === 0 ? "paused" : "[PAUSED - CAP]"}
              aria-label={`Word ${i + 1}`}
              className="h-11 min-w-0 flex-1 border-0 bg-transparent font-mono text-sm outline-none placeholder:font-sans placeholder:text-muted-foreground/70"
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => removeAt(i)}
              disabled={rows.length === 1 && !word}
              aria-label={`Remove word ${i + 1}`}
              className="h-8 w-8 shrink-0 p-0 text-muted-foreground hover:text-destructive"
            >
              <X className="h-4 w-4" />
            </Button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={() => onChange([...rows, ""])}
        className="flex w-full items-center gap-1.5 border-t border-border/60 bg-muted/30 px-3 py-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
      >
        <Plus className="h-3.5 w-3.5" /> Add another word
      </button>
    </div>
  );
};

export default RuleWordList;
